import {
  Box,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material'
import type { ReactNode, FC } from 'react'
import { useEffect, useMemo, useState } from 'react'
import type { AIConfig, AIContext, AIContextSettings, AIFieldMeta } from '../schema/ai-schema.js'
import {
  aiContexts,
  aiContextDescriptions,
  aiContextFieldConfig,
  personalities,
} from '../schema/ai-schema.js'
import { aiModelsSchema } from '../schema/providers.js'

const modelOptions = aiModelsSchema.options

/** Extract value from an input change event without depending on DOM types */
const getInputValue = (e: unknown) => (e as { target: { value: string } }).target.value

/**
 * Text field with local state for fast typing.
 * Only propagates to parent on blur to avoid re-rendering the entire form on every keystroke.
 */
const LocalTextField: FC<{
  meta: AIFieldMeta
  value: string | undefined
  globalValue: string | undefined
  showGlobalHint: boolean
  onChange: (value: string | undefined) => void
}> = ({ meta, value, globalValue, showGlobalHint, onChange }) => {
  const [localValue, setLocalValue] = useState(value ?? '')

  // Sync local state when the external value changes (e.g., collaborative edits, config refresh)
  useEffect(() => {
    setLocalValue(value ?? '')
  }, [value])

  return (
    <>
      <TextField
        fullWidth
        size="small"
        multiline
        minRows={2}
        maxRows={6}
        label={meta.label}
        placeholder={meta.placeholder}
        value={localValue}
        onChange={e => {
          const v = getInputValue(e)
          setLocalValue(v)
          onChange(v || undefined)
        }}
        helperText={meta.description}
      />
      {!localValue && globalValue && showGlobalHint && (
        <Chip
          label={`Using global: "${globalValue.length > 40 ? globalValue.slice(0, 40) + '...' : globalValue}"`}
          size="small"
          variant="outlined"
          sx={{ mt: 0.5, fontSize: '0.7rem' }}
        />
      )}
    </>
  )
}

interface AIConfigProps {
  defaultValue?: AIConfig
  onChange?: (config: AIConfig) => void
  /** Optional: render a preview panel below the form fields */
  renderPreview?: (props: { context: AIContext; settings: AIContextSettings }) => ReactNode
}

export const AIConfigForm: FC<AIConfigProps> = props => {
  const { defaultValue, onChange, renderPreview } = props

  const [tab, setTab] = useState<AIContext>('global')
  const defaultContexts: Record<AIContext, AIContextSettings> = {
    global: {},
    'song:section': {},
    'song:line': {},
    'song:image': {},
    'song:music': {},
    advisor: {},
  }
  const [config, setConfig] = useState<AIConfig>({
    ...defaultValue,
    contexts: { ...defaultContexts, ...defaultValue?.contexts },
  })

  const contexts = config.contexts
  const selectedContext = useMemo(() => contexts[tab] ?? {}, [contexts, tab])
  const globalContext = useMemo(() => contexts.global ?? {}, [contexts])
  const fieldConfig = aiContextFieldConfig[tab]
  const contextDesc = aiContextDescriptions[tab]

  const updateField = (field: keyof AIContextSettings, value: string | undefined) => {
    const updated = { ...selectedContext, [field]: value || undefined }
    const newConfig = { contexts: { ...config.contexts, [tab]: updated } }
    setConfig(newConfig)
    onChange?.(newConfig)
  }

  const renderField = (field: keyof AIContextSettings) => {
    const meta = fieldConfig[field]
    if (!meta) {
      return null
    }

    const value = selectedContext[field] as string | undefined
    const globalValue = tab !== 'global' ? (globalContext[field] as string | undefined) : undefined

    if (field === 'model') {
      return (
        <FormControl key={field} fullWidth size="small" sx={{ mb: 3 }}>
          <InputLabel>{meta.label}</InputLabel>
          <Select
            value={value ?? ''}
            label={meta.label}
            onChange={e => updateField(field, e.target.value || undefined)}
          >
            <MenuItem value="">
              <em>{globalValue ? `Default (${globalValue})` : 'Default'}</em>
            </MenuItem>
            {modelOptions.map(m => (
              <MenuItem key={m} value={m}>
                {m}
              </MenuItem>
            ))}
          </Select>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
            {meta.description}
          </Typography>
        </FormControl>
      )
    }

    if (field === 'personality') {
      return (
        <FormControl key={field} fullWidth size="small" sx={{ mb: 3 }}>
          <InputLabel>{meta.label}</InputLabel>
          <Select
            value={value ?? ''}
            label={meta.label}
            onChange={e => updateField(field, e.target.value || undefined)}
          >
            <MenuItem value="">
              <em>{globalValue ? `Default (${globalValue})` : 'None'}</em>
            </MenuItem>
            {personalities.map(p => (
              <MenuItem key={p} value={p} sx={{ textTransform: 'capitalize' }}>
                {p}
              </MenuItem>
            ))}
          </Select>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
            {meta.description}
          </Typography>
        </FormControl>
      )
    }

    // Multiline text fields — uses local state for fast typing, propagates on blur
    return (
      <Box key={`${tab}-${field}`} sx={{ mb: 3 }}>
        <LocalTextField
          meta={meta}
          value={value}
          globalValue={globalValue}
          showGlobalHint={tab !== 'global'}
          onChange={v => updateField(field, v)}
        />
      </Box>
    )
  }

  const visibleFields = Object.keys(fieldConfig) as (keyof AIContextSettings)[]

  return (
    <Box sx={{ display: 'flex', flexDirection: 'row', height: '100%' }}>
      <Tabs value={tab} onChange={(_, value) => setTab(value)} orientation="vertical" sx={{ minWidth: 140 }}>
        {aiContexts.map(context => (
          <Tab key={context} label={aiContextDescriptions[context].label} value={context} />
        ))}
      </Tabs>

      <Box sx={{ flex: 1, ml: 3, overflow: 'auto' }}>
        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
          {contextDesc.label}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {contextDesc.description}
        </Typography>

        {visibleFields.map(field => renderField(field))}

        {renderPreview?.({ context: tab, settings: selectedContext })}
      </Box>
    </Box>
  )
}

export default AIConfigForm
